import { useTranslation } from 'react-i18next'

export interface ApplyChannel {
  id: string
  name: string
  isMaster: boolean
  jtlSynced: boolean
  hasBulletMapping: boolean
  /** Optional readable identifier (e.g. JTL channel id like "2-amazon-de"). */
  externalChannelId?: string
}

export interface RemakeApplyChannelPickerProps {
  channels: ApplyChannel[]
  activeId: string
  onChange: (id: string) => void
}

export function RemakeApplyChannelPicker({
  channels,
  activeId,
  onChange,
}: RemakeApplyChannelPickerProps) {
  const { t } = useTranslation()
  return (
    <div
      className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 px-6 py-3 flex items-center gap-2 flex-wrap"
      role="tablist"
      aria-label={t('remakeApply.channelPicker.label')}
    >
      <span className="text-xs text-neutral-500 mr-1">
        <span>{t('remakeApply.channelPicker.label')}</span>
        <span aria-hidden="true">:</span>
      </span>
      {channels.map((c) => {
        const isActive = c.id === activeId
        const isDisabled = !c.jtlSynced
        const titleParts: string[] = []
        if (isDisabled) titleParts.push(t('remakeApply.channelPicker.noJtlSync'))
        if (c.jtlSynced && !c.hasBulletMapping)
          titleParts.push(t('remakeApply.channelPicker.mappingMissing'))
        const title = titleParts.join(' · ') || undefined
        return (
          <button
            key={c.id}
            type="button"
            disabled={isDisabled}
            aria-pressed={isActive}
            title={title}
            onClick={() => onChange(c.id)}
            className={[
              'px-3 py-1.5 text-sm rounded-full transition-colors',
              isActive
                ? 'bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900'
                : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700',
              isDisabled ? 'opacity-50 cursor-not-allowed' : '',
              !isDisabled && !c.hasBulletMapping
                ? 'ring-1 ring-amber-300 dark:ring-amber-700'
                : '',
            ].join(' ')}
          >
            {c.name}
          </button>
        )
      })}
    </div>
  )
}
